import { useEffect } from 'react'
import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'

import {
  AddUserProps,
  DDE_USER_ROLES,
  USER_ROLE,
  UserProps,
  UserRole
} from '@img-arena/consoles-api-dde'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { FormikTextField } from '@/components/inputs/FormikTextField'
import { OperatorAutocomplete } from '@/components/inputs/OperatorAutocomplete'
import { SelectBox } from '@/components/inputs/SelectBox'
import { ApiError } from '@/service/ApiError'
import { ddeApi } from '@/service/ddeApi'
import type { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps, getFormError, onFieldChange } from '@/utils/forms/formik'

import { userFormValidationSchema } from './userForm.validation'

export const roleSelectTestId = 'role-select'

export type DDE_FormikValues = {
  userName: string
  email: string

  role: UserRole | null
  operator: string | null
}

export const DDEUserForm = ({
  onClose,
  onSubmit,
  open = false,
  defaultValues,
  disabledProperties
}: FormDialogProps<UserProps<never>, undefined, keyof DDE_FormikValues>) => {
  const { enqueueSnackbar } = useSnackbar()

  const formik = useFormik<DDE_FormikValues>({
    initialValues: {
      userName: defaultValues?.userName || '',
      email: defaultValues?.userId || '',

      role: (defaultValues?.role?.roleName as UserRole) || null,
      // TODO: go back here once operator autocomplete is implemented
      operator: defaultValues?.role?.operatorId?.toString() || null
    },

    validationSchema: userFormValidationSchema.DDE,

    onSubmit: async (values, { setSubmitting }) => {
      if (!values.role) return

      try {
        setSubmitting(true)

        const role = {
          roleName: values.role as UserRole,
          ...(values.role === USER_ROLE.OPERATOR && values.operator
            ? { operatorId: Number(values.operator) }
            : {})
        }

        const model = {
          emailAddress: values.email,
          userName: values.userName,
          role
        } satisfies AddUserProps['data']

        await ddeApi.endpoints.users.add(model)

        onSubmit?.()
        enqueueSnackbar(`User ${values.userName} has been created`, { variant: 'success' })
        handleClose()
      } catch (e) {
        const errorMessage = ['Failed to create user', (e as unknown as ApiError)?.message]
          .filter(Boolean)
          .join(': ')
        enqueueSnackbar(errorMessage, { variant: 'error' })
        // Throw the error again to be caught by dialog and doesn't close it
        throw errorMessage
      } finally {
        setSubmitting(false)
      }
    }
  })

  useEffect(() => {
    if (!defaultValues?.role?.operatorId && !disabledProperties?.includes('operator')) {
      formik.setFieldValue('operator', null, true)
    }
  }, [formik.values.role, defaultValues?.role?.operatorId, disabledProperties])

  const isEditMode = !!defaultValues?.userName
  const title = isEditMode ? 'Edit User' : 'Add User'

  const handleClose = () => {
    formik.resetForm({
      values: {
        userName: '',
        email: '',
        role: null,
        operator: null
      }
    })
    onClose?.()
  }

  return (
    <ConsoleDialog
      open={open}
      title={title}
      onClose={handleClose}
      SaveButtonProps={formikSaveButtonProps(formik)}
    >
      <FormikTextField label="Username" required name="userName" formik={formik} />
      <FormikTextField label="Email" required name="email" formik={formik} />
      <SelectBox
        label="Role"
        name="role"
        required
        fullWidth
        value={formik.values.role}
        onChange={(value) => onFieldChange(formik, 'role', value)}
        options={DDE_USER_ROLES}
        error={getFormError(formik, 'role')}
        testId={roleSelectTestId}
        excludeSelected
        disabled={disabledProperties?.includes('role')}
      />
      <OperatorAutocomplete
        defaultValue={formik.values.operator}
        onChange={(value) => onFieldChange(formik, 'operator', value)}
        required={formik.values.role === USER_ROLE.OPERATOR}
        disabled={
          formik.values.role !== USER_ROLE.OPERATOR || disabledProperties?.includes('operator')
        }
        disabledInfo='Please select "Operator" role to enable this field'
        error={getFormError(formik, 'operator')}
      />
    </ConsoleDialog>
  )
}
